import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { tableService } from '../services/table';
import { getActiveRestaurantId } from '../utils/roleAccess';
import { itemService } from '../services/item';
import { orderService } from '../services/order';
import { printService, willUseShare } from '../services/print';
import { kitchenService } from '../services/kitchen';
import { itemCategoryService } from '../services/itemCategory';
import { Table, Item, OrderItem, Order, PaymentMode, Kitchen } from '../types';
import {
  ShoppingCart,
  Users,
  Plus,
  Minus,
  X,
  Check,
  Search,
  Edit3,
  Clock3,
  AlertCircle,
  Trash2,
  ChefHat  
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { InlineLoader, ButtonLoader, SkeletonCard } from '../components/LoadingStates';
import VegIcon from '../assets/Veg.png';
import NonVegIcon from '../assets/Non_veg.png';

interface SelectedItem extends OrderItem {
  itemNotes?: string;
  isNewItem?: boolean;
  kitchenId?: string;
  itemType?: string;
  vegType?: string;
  item?: Item; // kept if you ever need the full item
}

interface TableWithOrder extends Table {
  currentOrders?: Order[];
}

interface CustomerDetails {
  customerId?: string;
  customerName: string;
  customerMobile: string;
  customerEmail?: string;
  customerAddress?: string;
}

export const PlaceOrder: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showTableModal, setShowTableModal] = useState(false);
  const [showOrderSummaryModal, setShowOrderSummaryModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState<TableWithOrder | null>(null);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedVegType, setSelectedVegType] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [orderNotes, setOrderNotes] = useState('');
  const [showOrderNotes, setShowOrderNotes] = useState(false);
  const [paymentMode] = useState<PaymentMode>('CASH');
  const [discountPercent] = useState<number>(0);
  const [discountAmount] = useState<number>(0);
  const [customerDetails] = useState<CustomerDetails>({
    customerName: '',
    customerMobile: '',
    customerEmail: '',
    customerAddress: ''
  });

  // NEW: edit/update flags + existing order reference + confirm modal
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [existingOrder, setExistingOrder] = useState<Order | null>(null);
  const [showExistingOrderChoice, setShowExistingOrderChoice] = useState(false);

  const activeRestaurantId = getActiveRestaurantId();

  // Fetch tables filtered by active restaurant when available
  const { data: allTables, isLoading: tablesLoading } = useQuery(
    ['tables', user?.organizationCode, activeRestaurantId],
    () => tableService.getTables({
      org: user?.organizationCode!,
      ...(activeRestaurantId && { restaurantId: activeRestaurantId }),
    }),
    { enabled: !!user?.organizationCode }
  );

  // Fetch open orders (NOTE: your backend returns Page<Order>, so normalize)
  const { data: openOrders } = useQuery(
    ['openOrders', user?.organizationCode, activeRestaurantId],
    () => orderService.getOpenOrders(user?.organizationCode!, activeRestaurantId),
    { enabled: !!user?.organizationCode }
  );

  // Fetch all kitchens
  const { data: kitchens } = useQuery(
    ['kitchens', user?.organizationCode],
    () => kitchenService.getKitchens(user?.organizationCode!),
    { enabled: !!user?.organizationCode }
  );

  // Fetch item categories
  const { data: categories } = useQuery(
    ['itemCategories', user?.organizationCode],
    () => itemCategoryService.getActiveCategories(user?.organizationCode!),
    { enabled: !!user?.organizationCode }
  );

  // normalize open orders to a simple array
  type PageLike<T> = { content?: T[] } & Record<string, unknown>;

  const openOrdersList: Order[] = useMemo(() => {
    const raw = openOrders as PageLike<Order> | Order[] | undefined;
    if (Array.isArray(raw)) return raw;
    if (raw && Array.isArray(raw.content)) return raw.content;
    return [];
  }, [openOrders]);

  // Fetch all items or search based on term
  const { data: items, isLoading: itemsLoading } = useQuery(
    ['items', user?.organizationCode, debouncedSearchTerm, selectedVegType, selectedCategory],
    async () => {
      if (debouncedSearchTerm.trim()) {
        return itemService.searchItems(user?.organizationCode!, debouncedSearchTerm.trim());
      } else {
        // Fetch items with filters
        return itemService.getItems({
          org: user?.organizationCode!,
          ...(selectedVegType && { vegType: selectedVegType }),
          ...(selectedCategory && { categoryId: selectedCategory })
        });
      }
    },
    {
      enabled: !!user?.organizationCode && !!selectedTable,
      keepPreviousData: true
    }
  );

  // Create kitchen map for quick lookup
  const kitchenMap = useMemo(() => {
    if (!kitchens) return {} as Record<string, Kitchen>;
    return kitchens.reduce((acc, kitchen) => {
      acc[kitchen.id] = kitchen;
      return acc;
    }, {} as Record<string, Kitchen>);
  }, [kitchens]);

  // Combine tables with their current orders (using normalized list)
  const tables: TableWithOrder[] = useMemo(() => {
    if (!allTables) return [];
    return allTables.map(table => {
      const currentOrders = openOrdersList.filter(
        o => String(o.tableId) === String(table.id)
      );
      return { ...table, currentOrders };
    });
  }, [allTables, openOrdersList]);


  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Place order mutation
  const placeOrderMutation = useMutation(
    async () => {
      if (!activeRestaurantId) {
        throw new Error('Please select a restaurant before placing an order');
      }

      if (!selectedTable || selectedItems.length === 0) {
        throw new Error('Please select a table and add items');
      }

      if (!selectedTable.active) {
        throw new Error('Cannot place order on an inactive table');
      }

      const orderData = {
        organizationCode: user?.organizationCode!,
        tableId: selectedTable.id,
        paymentMode,
        notes: orderNotes || undefined,
        discountPercent: discountPercent > 0 ? discountPercent : undefined,
        discountAmount: discountAmount > 0 ? discountAmount : undefined,
        customerId: customerDetails.customerId || undefined,
        customerName: customerDetails.customerName || undefined,
        customerMobile: customerDetails.customerMobile || undefined,
        customerEmail: customerDetails.customerEmail || undefined,
        customerAddress: customerDetails.customerAddress || undefined,
        ...(activeRestaurantId && { restaurantId: activeRestaurantId }),
        items: selectedItems.map(item => ({
          itemId: item.itemId!,
          quantity: item.quantity,
          specialNotes: item.itemNotes || undefined
        }))
      };

      return orderService.placeOrder(orderData);
    },
    {
      onSuccess: () => {
        toast.success('Order placed successfully!');
        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries(['openOrders', user?.organizationCode]);
        queryClient.invalidateQueries(['tables', user?.organizationCode]);
        navigate('/orders');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to place order');
      }
    }
  );

  // Place order and print KOT mutation
  const placeOrderAndPrintMutation = useMutation(
    async () => {
      if (!activeRestaurantId) {
        throw new Error('Please select a restaurant before placing an order');
      }

      if (!selectedTable || selectedItems.length === 0) {
        throw new Error('Please select a table and add items');
      }

      if (!selectedTable.active) {
        throw new Error('Cannot place order on an inactive table');
      }

      const orderData = {
        organizationCode: user?.organizationCode!,
        tableId: selectedTable.id,
        paymentMode,
        notes: orderNotes || undefined,
        discountPercent: discountPercent > 0 ? discountPercent : undefined,
        discountAmount: discountAmount > 0 ? discountAmount : undefined,
        customerId: customerDetails.customerId || undefined,
        customerName: customerDetails.customerName || undefined,
        customerMobile: customerDetails.customerMobile || undefined,
        customerEmail: customerDetails.customerEmail || undefined,
        customerAddress: customerDetails.customerAddress || undefined,
        ...(activeRestaurantId && { restaurantId: activeRestaurantId }),
        items: selectedItems.map(item => ({
          itemId: item.itemId!,
          quantity: item.quantity,
          specialNotes: item.itemNotes || undefined
        }))
      };

      return orderService.placeOrder(orderData);
    },
    {
      onSuccess: async (order: any) => {
        toast.success('Order placed successfully!');

        // Print/Share KOT
        try {
          await printService.printKOT(order.orderId, user?.organizationCode!, true);
          toast.success(willUseShare() ? 'KOT shared successfully!' : 'KOT sent to print!');
        } catch (error) {
          console.error('Failed to print/share KOT:', error);
          toast.error(willUseShare() ? 'Order placed but sharing failed' : 'Order placed but printing failed');
        }

        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries(['openOrders', user?.organizationCode]);
        queryClient.invalidateQueries(['tables', user?.organizationCode]);
        navigate('/orders');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to place order');
      }
    }
  );

  // NEW: Update order mutation
  const updateOrderMutation = useMutation(
    async () => {
      if (!existingOrder) throw new Error('No existing order to update');

      const updateData = {
        notes: orderNotes || undefined,
        // if you add discount controls on this screen later, map them below:
        // discountPercent: discountPercent > 0 ? discountPercent : undefined,
        // discountAmount: discountAmount > 0 ? discountAmount : undefined,
        items: selectedItems.map(it => ({
          itemId: it.itemId,
          quantity: it.quantity,
          specialNotes: it.itemNotes || undefined
        }))
      };

      return orderService.updateOrder(existingOrder.id, updateData);
    },
    {
      onSuccess: () => {
        toast.success('Order updated successfully!');
        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries(['openOrders', user?.organizationCode]);
        queryClient.invalidateQueries(['tables', user?.organizationCode]);
        navigate('/orders');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.message || 'Failed to update order');
      }
    }
  );

  const handleTableSelect = (table: TableWithOrder) => {
    if (!table.active) {
      return;
    }

    const hasOrders = (table.currentOrders?.length ?? 0) > 0;
    setSelectedTable(table);
    if (hasOrders) {
      setShowExistingOrderChoice(true);
    } else {
      setIsEditingExisting(false);
      setExistingOrder(null);
      setSelectedItems([]);
      setOrderNotes('');
      setShowTableModal(false);
    }
  };
  const handleChangeTable = () => {
    setShowTableModal(true);
  };

  // Load existing order items into the right pane for editing
  const loadExistingOrderForEdit = (order: Order) => {
    setIsEditingExisting(true);
    setExistingOrder(order);

    const mapped: SelectedItem[] = order.items.map((it: any) => ({
      itemId: it.itemId,
      name: it.name,
      quantity: it.quantity,
      unitPrice: it.unitPrice,
      specialNotes: it.specialNotes,
      itemNotes: it.specialNotes
    }));

    setSelectedItems(mapped);
    setOrderNotes(order.notes ?? '');
    setShowExistingOrderChoice(false);
    setShowTableModal(false);
  };

  const addItem = (item: Item) => {
    const existingItem = selectedItems.find(si => si.itemId === item.id);
    if (existingItem) {
      setSelectedItems(prev =>
        prev.map(si =>
          si.itemId === item.id
            ? { ...si, quantity: si.quantity + 1 }
            : si
        )
      );
    } else {
      setSelectedItems(prev => [...prev, {
        itemId: item.id,
        name: item.name,
        quantity: 1,
        unitPrice: item.price,
        specialNotes: '',
        itemNotes: '',
        item,
        isNewItem: false
      }]);
    }
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      setSelectedItems(prev => prev.filter(si => si.itemId !== itemId));
      return;
    }
    setSelectedItems(prev =>
      prev.map(si =>
        si.itemId === itemId
          ? { ...si, quantity }
          : si
      )
    );
  };

  const updateItemNotes = (itemId: string, notes: string) => {
    setSelectedItems(prev =>
      prev.map(si =>
        si.itemId === itemId
          ? { ...si, itemNotes: notes }
          : si
      )
    );
  };

  const getTableStatusDisplay = (table: TableWithOrder) => {
    const hasOrders = (table.currentOrders?.length ?? 0) > 0;
    if (hasOrders) {
      return { text: 'Occupied', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: AlertCircle };
    }
    // the rest unchanged…
    switch (table.status) {
      case 'AVAILABLE': return { text: 'Available', color: 'bg-green-100 text-green-800 border-green-200', icon: Clock3 };
      case 'IN_USE': return { text: 'In Use', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: AlertCircle };
      case 'RESERVED': return { text: 'Reserved', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: AlertCircle };
      case 'OUT_OF_SERVICE': return { text: 'Out of Service', color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle };
      default: return { text: 'Available', color: 'bg-green-100 text-green-800 border-green-200', icon: Clock3 };
    }
  };


  // Calculate totals
  const subtotal = selectedItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
  const discountAmountCalculated = discountPercent > 0 ? (subtotal * discountPercent / 100) : discountAmount;
  const totalAmount = subtotal - discountAmountCalculated;

  if (tablesLoading) {
    return (
      <div className="min-h-screen p-3 sm:p-4 md:p-6" style={{ backgroundColor: currentTheme.colors.background.primary }}>
        <div className="space-y-4 sm:space-y-6">
          {/* Header Skeleton */}
          <SkeletonCard />

          {/* Tables Grid Skeleton */}
          <div>
            <div className="mb-4">
              <SkeletonCard />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
              {[...Array(12)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          </div>

          {/* Items Section Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2">
              <SkeletonCard />
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                {[...Array(6)].map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            </div>
            <div>
              <SkeletonCard />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const primaryActionLoading = isEditingExisting
    ? updateOrderMutation.isLoading
    : (placeOrderMutation.isLoading || placeOrderAndPrintMutation.isLoading);
  const primaryActionDisabled = primaryActionLoading || !selectedTable || selectedItems.length === 0;

  return (
    <div className="min-h-screen" style={{ backgroundColor: currentTheme.colors.background.primary }}>
      {/* Header */}
      <div className="p-3 sm:p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold flex items-center" style={{ color: currentTheme.colors.text.primary }}>
              <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 mr-2" style={{ color: currentTheme.colors.primary[600] }} />
              Place Order
            </h1>
            <p className="text-xs sm:text-sm mt-1" style={{ color: currentTheme.colors.text.secondary }}>
              {selectedTable ? `Ordering for ${selectedTable.name}` : 'Select a table to start ordering'}
            </p>
          </div>
          {selectedTable && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <div
                className="px-3 sm:px-4 py-2 rounded-lg border"
                style={{
                  backgroundColor: currentTheme.colors.background.card,
                  borderColor: currentTheme.colors.border.primary
                }}
              >
                <span className="text-xs sm:text-sm font-medium" style={{ color: currentTheme.colors.text.secondary }}>
                  Selected Table:
                </span>
                <span className="ml-2 font-bold text-sm sm:text-base" style={{ color: currentTheme.colors.text.primary }}>
                  {selectedTable.name}
                </span>
              </div>
              <button
                onClick={handleChangeTable}
                className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                style={{ backgroundColor: currentTheme.colors.primary[600] }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700]}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600]}
              >
                <Edit3 className="h-4 w-4 mr-1" />
                Change
              </button>
            </div>
          )}
        </div>
      </div>

      {!selectedTable ? (
        /* Full Page Table Selection */
        <div className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
          <div
            className="rounded-lg sm:rounded-xl shadow-sm border p-4 sm:p-6"
            style={{
              backgroundColor: currentTheme.colors.background.card,
              borderColor: currentTheme.colors.border.primary
            }}
          >
            <div className="mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-semibold mb-2" style={{ color: currentTheme.colors.text.primary }}>
                Select a Table
              </h2>
              <p className="text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                Choose a table to start placing your order
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {tables?.map(table => {
                const status = getTableStatusDisplay(table);
                const StatusIcon = status.icon;
                const isInactive = !table.active;

                return (
                  <div
                    key={table.id}
                    onClick={() => handleTableSelect(table)}
                    className={clsx(
                      "p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all",
                      isInactive ? "opacity-50 cursor-not-allowed" : "cursor-pointer transform hover:scale-105 hover:shadow-lg"
                    )}
                    style={{
                      backgroundColor: isInactive ? '#fef2f2' : currentTheme.colors.background.card,
                      borderColor: isInactive ? '#ef4444' : currentTheme.colors.border.primary
                    }}
                    onMouseEnter={(e) => {
                      if (!isInactive) {
                        e.currentTarget.style.borderColor = currentTheme.colors.primary[300];
                        e.currentTarget.style.backgroundColor = currentTheme.colors.primary[50];
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isInactive) {
                        e.currentTarget.style.borderColor = currentTheme.colors.border.primary;
                        e.currentTarget.style.backgroundColor = currentTheme.colors.background.card;
                      }
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-base sm:text-lg font-bold truncate" style={{ color: currentTheme.colors.text.primary }}>
                        {table.name}
                      </span>
                    </div>

                    <div className="flex items-center justify-between mb-2 gap-2">
                      <span className="text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                        {table.capacity} seats
                      </span>
                    </div>

                    <div className={clsx('px-2 py-1 rounded-lg text-xs font-semibold w-full text-center border', status.color)}>
                      <div className="flex items-center justify-center space-x-1">
                        <StatusIcon className="h-3 w-3" />
                        <span>{status.text}</span>
                      </div>
                    </div>

                    {(table.currentOrders?.length ?? 0) > 0 && (
                      <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded mt-2 font-medium border border-orange-200">
                        <div className="flex items-center justify-center space-x-1">
                          <Users className="h-3 w-3" />
                          <span>
                            {table.currentOrders!.length === 1
                              ? `1 Active Order (${table.currentOrders![0].items.length} items)`
                              : `${table.currentOrders!.length} Active Orders`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        /* Two Column Layout - Menu Items & Order Summary */
        <div className="flex flex-col lg:flex-row h-auto lg:h-[calc(100vh-140px)] px-3 sm:px-4 md:px-6 pb-28 gap-3 sm:gap-4 md:gap-6">
          {/* Left Side - Menu Items */}
          <div className="flex-1 flex flex-col min-h-[400px] lg:min-h-0">
            <div
              className="rounded-lg sm:rounded-xl shadow-sm border flex-1 flex flex-col"
              style={{
                backgroundColor: currentTheme.colors.background.card,
                borderColor: currentTheme.colors.border.primary
              }}
            >
              {/* Search Header */}
              <div className="p-4 sm:p-6 border-b" style={{ borderColor: currentTheme.colors.border.primary }}>
                <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4" style={{ color: currentTheme.colors.text.primary }}>
                  {isEditingExisting ? 'Update Items' : 'Select Items'}
                </h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search food items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:border-transparent text-sm sm:text-base"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.primary,
                      color: currentTheme.colors.text.primary,
                      '--tw-ring-color': currentTheme.colors.primary[500]
                    } as React.CSSProperties}
                  />
                </div>

                {/* Filter Dropdowns */}
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <select
                    value={selectedVegType}
                    onChange={(e) => setSelectedVegType(e.target.value)}
                    className="px-2 py-2 border rounded-lg text-xs sm:text-sm focus:ring-2 focus:border-transparent"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.primary,
                      color: currentTheme.colors.text.primary
                    }}
                  >
                    <option value="">All Types</option>
                    <option value="VEG">Veg</option>
                    <option value="NON_VEG">Non-Veg</option>
                    <option value="VEGAN">Vegan</option>
                  </select>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-2 py-2 border rounded-lg text-xs sm:text-sm focus:ring-2 focus:border-transparent"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.primary,
                      color: currentTheme.colors.text.primary
                    }}
                  >
                    <option value="">All Categories</option>
                    {categories?.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>

                {itemsLoading ? (
                  <div className="mt-2">
                    <InlineLoader text={searchTerm ? 'Searching...' : 'Loading items...'} />
                  </div>
                ) : (
                  <p className="text-xs sm:text-sm mt-2" style={{ color: currentTheme.colors.text.secondary }}>
                    {searchTerm ? `Showing results for "${searchTerm}"` : 'Showing all available items'}
                  </p>
                )}
              </div>

              {/* Items List */}
              <div className="flex-1 overflow-y-auto p-4 mb-12 sm:p-6">
                {itemsLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                    {[...Array(6)].map((_, i) => (
                      <SkeletonCard key={i} />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                    {items?.map((item: Item) => {
                      const addedItem = selectedItems.find(si => si.itemId === item.id);
                      const isAdded = !!addedItem;
                      const kitchen = kitchenMap[item.kitchenId];
                      const isKitchenInactive = kitchen ? !kitchen.active : false;
                      const isUnavailable = !item.available || isKitchenInactive;

                      return (
                        <div
                          key={item.id}
                          className={`p-3 sm:p-4 rounded-lg border-2 transition-all ${!isUnavailable ? 'hover:shadow-md' : ''}`}
                          style={{
                            borderColor: isUnavailable
                              ? '#fca5a5'
                              : isAdded
                                ? '#86efac'
                                : currentTheme.colors.border.primary,
                            backgroundColor: isUnavailable
                              ? '#fef2f2'
                              : isAdded
                                ? '#f0fdf4'
                                : currentTheme.colors.background.card,
                            opacity: isUnavailable ? 0.6 : 1
                          }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <h4
                              className="text-sm sm:text-base font-semibold truncate flex-1"
                              style={{ color: currentTheme.colors.text.primary }}
                            >
                              {item.name}
                            </h4>
                            {item.vegType === 'VEG' && <img src={VegIcon} alt="Veg" className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />}
                            {item.vegType === 'NON_VEG' && <img src={NonVegIcon} alt="Non-Veg" className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />}
                          </div>

                          <div className="flex items-center gap-1 mb-2">
                            <ChefHat className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" style={{ color: currentTheme.colors.text.secondary }} />
                            <p className="text-xs sm:text-sm truncate" style={{ color: currentTheme.colors.text.secondary }}>
                              {kitchen ? kitchen.name : (item.kitchenName || 'Unknown Kitchen')}
                            </p>
                          </div>

                          <div className="flex items-center justify-between mb-2">
                            <p
                              className="text-sm font-medium"
                              style={{ color: currentTheme.colors.text.secondary }}
                            >
                              ₹{item.price}
                            </p>

                            {isUnavailable && (
                              <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-red-100 text-red-800 text-[10px] sm:text-xs font-medium rounded-full border border-red-200 whitespace-nowrap">
                                N/A
                              </span>
                            )}

                            {!isUnavailable && !isAdded && (
                              <button
                                onClick={() => addItem(item)}
                                className="p-2 sm:p-3 text-white rounded-lg transition-colors flex-shrink-0 transform hover:scale-105"
                                style={{
                                  backgroundColor: currentTheme.colors.primary[600]
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                              >
                                <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                              </button>
                            )}

                            {!isUnavailable && isAdded && addedItem && (
                              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                                <button
                                  onClick={() => updateQuantity(item.id, addedItem.quantity - 1)}
                                  className="p-1.5 sm:p-2 rounded-lg transition-colors"
                                  style={{ backgroundColor: addedItem.quantity === 1 ? '#dc2626' : currentTheme.colors.primary[600], color: '#ffffff' }}
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = addedItem.quantity === 1 ? '#b91c1c' : currentTheme.colors.primary[700])}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = addedItem.quantity === 1 ? '#dc2626' : currentTheme.colors.primary[600])}
                                >
                                  {addedItem.quantity === 1 ? (
                                    <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                                  ) : (
                                    <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
                                  )}
                                </button>
                                <span className="text-sm sm:text-base font-bold w-6 sm:w-8 text-center" style={{ color: currentTheme.colors.text.primary }}>
                                  {addedItem.quantity}
                                </span>
                                <button
                                  onClick={() => updateQuantity(item.id, addedItem.quantity + 1)}
                                  className="p-1.5 sm:p-2 rounded-lg transition-colors"
                                  style={{ backgroundColor: currentTheme.colors.primary[600], color: '#ffffff' }}
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                                >
                                  <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                                </button>
                              </div>
                            )}
                          </div>

                          {!isUnavailable && isAdded && addedItem && (
                            <input
                              type="text"
                              placeholder="Add a note... (optional)"
                              value={addedItem.itemNotes || ''}
                              onChange={(e) => updateItemNotes(item.id, e.target.value)}
                              className="w-full px-2 py-1 border rounded text-xs mt-1"
                              style={{
                                borderColor: currentTheme.colors.border.primary,
                                backgroundColor: currentTheme.colors.background.primary,
                                color: currentTheme.colors.text.primary
                              }}
                            />
                          )}

                          {isUnavailable && (
                            <p className="text-[10px] sm:text-xs font-medium mt-1 text-red-600">
                              {isKitchenInactive ? '❌ Kitchen not available' : '❌ Unavailable'}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Floating Order Summary Button (All screen sizes) */}
          {selectedItems.length > 0 && (
            <div className="fixed bottom-0 left-0 right-0 flex justify-center p-3 sm:p-4 z-40">
              <button
                onClick={() => setShowOrderSummaryModal(true)}
                className="w-full sm:max-w-md md:max-w-lg py-4 text-white rounded-lg transition-all flex items-center justify-between px-6 shadow-2xl"
                style={{ backgroundColor: currentTheme.colors.primary[600] }}
              >
                <div className="flex items-center space-x-2">
                  <ShoppingCart className="h-5 w-5" />
                  <span className="font-semibold">{selectedItems.length} {selectedItems.length === 1 ? 'Item' : 'Items'}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-lg font-bold">₹{totalAmount.toFixed(2)}</span>
                  <span className="text-sm">View Order →</span>
                </div>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Order Summary Modal (All screen sizes) */}
      {showOrderSummaryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50" onClick={() => setShowOrderSummaryModal(false)}>
          <div
            className="w-full sm:max-w-md md:max-w-lg bg-white rounded-t-2xl max-h[90vh] overflow-hidden animate-slide-up"
            style={{ backgroundColor: currentTheme.colors.background.card }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10" style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.card }}>
              <h2 className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                {isEditingExisting ? `Update Order (${selectedItems.length} items)` : `Order Summary (${selectedItems.length} items)`}
              </h2>
              <button
                onClick={() => setShowOrderSummaryModal(false)}
                className="p-2 rounded-lg transition-colors"
                style={{ backgroundColor: currentTheme.colors.background.secondary }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Items List */}
            <div className="overflow-y-auto max-h-[calc(90vh-200px)] p-4 space-y-3">
              {selectedItems.map(item => (
                <div
                  key={item.itemId}
                  className="p-3 rounded-lg border"
                  style={{
                    backgroundColor: currentTheme.colors.background.primary,
                    borderColor: currentTheme.colors.border.primary
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm truncate" style={{ color: currentTheme.colors.text.primary }}>
                        {item.name}
                      </h4>
                      <p className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                        ₹{item.unitPrice} each
                      </p>
                    </div>
                    <button
                      onClick={() => setSelectedItems(prev => prev.filter(si => si.itemId !== item.itemId))}
                      className="p-1 text-red-500 hover:text-red-700 flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Quantity Controls */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateQuantity(item.itemId, item.quantity - 1)}
                        className="p-1.5 rounded-lg transition-colors"
                        style={{ backgroundColor: currentTheme.colors.background.secondary }}
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="text-base font-bold w-6 text-center" style={{ color: currentTheme.colors.text.primary }}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.itemId, item.quantity + 1)}
                        className="p-1.5 rounded-lg transition-colors"
                        style={{ backgroundColor: currentTheme.colors.background.secondary }}
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    <p className="text-base font-bold" style={{ color: currentTheme.colors.primary[600] }}>
                      ₹{(item.unitPrice * item.quantity).toFixed(2)}
                    </p>
                  </div>

                  {/* Item Notes */}
                  <textarea
                    placeholder="Special notes for item (optional)"
                    value={item.itemNotes || ''}
                    onChange={(e) => updateItemNotes(item.itemId, e.target.value)}
                    className="w-full p-2 border rounded text-xs resize-none"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.card,
                      color: currentTheme.colors.text.primary
                    }}
                    rows={2}
                  />
                </div>
              ))}
            </div>

            {/* Order Notes - Collapsible */}
            <div className="px-4 border-t" style={{ borderColor: currentTheme.colors.border.primary }}>
              {!showOrderNotes ? (
                <div className="py-3">
                  <button
                    onClick={() => setShowOrderNotes(true)}
                    className="text-sm font-medium hover:underline flex items-center gap-1"
                    style={{ color: currentTheme.colors.primary[600] }}
                  >
                    <Plus className="h-3 w-3" />
                    Add Any Message
                  </button>
                </div>
              ) : (
                <div className="py-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                     Any Special Message (Optional)
                    </label>
                    <button
                      onClick={() => {
                        setShowOrderNotes(false);
                        setOrderNotes('');
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <textarea
                    placeholder="Any special instructions..."
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    className="w-full p-2 border rounded-lg resize-none text-sm"
                    style={{
                      borderColor: currentTheme.colors.border.primary,
                      backgroundColor: currentTheme.colors.background.primary,
                      color: currentTheme.colors.text.primary
                    }}
                    rows={2}
                    autoFocus
                  />
                </div>
              )}
            </div>

            {/* Total and Primary Action */}
            <div className="p-4 border-t sticky bottom-0 bg-white" style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.card }}>
              <div className="flex justify-between items-center mb-3">
                <span className="text-lg font-bold" style={{ color: currentTheme.colors.text.primary }}>
                  Total:
                </span>
                <span className="text-xl font-bold" style={{ color: currentTheme.colors.primary[600] }}>
                  ₹{totalAmount.toFixed(2)}
                </span>
              </div>

              {isEditingExisting ? (
                <button
                  onClick={() => {
                    updateOrderMutation.mutate();
                    setShowOrderSummaryModal(false);
                  }}
                  disabled={primaryActionDisabled}
                  className="w-full py-3 text-white text-base font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ backgroundColor: currentTheme.colors.primary[600] }}
                >
                  {primaryActionLoading ? <ButtonLoader text="Updating..." /> : `Update Order (₹${totalAmount.toFixed(2)})`}
                </button>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => {
                      placeOrderMutation.mutate();
                      setShowOrderSummaryModal(false);
                    }}
                    disabled={primaryActionDisabled}
                    className="flex-1 py-3 text-white text-sm sm:text-base font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: currentTheme.colors.primary[600] }}
                  >
                    {placeOrderMutation.isLoading ? <ButtonLoader text="Processing..." /> : `Place Order`}
                  </button>
                  <button
                    onClick={() => {
                      placeOrderAndPrintMutation.mutate();
                      setShowOrderSummaryModal(false);
                    }}
                    disabled={primaryActionDisabled}
                    className="flex-1 py-3 text-white text-sm sm:text-base font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: currentTheme.colors.primary?.[600] || currentTheme.colors.primary[700] }}
                  >
                    {placeOrderAndPrintMutation.isLoading ? (
                      <ButtonLoader text={willUseShare() ? "Placing & Sharing..." : "Placing & Printing..."} />
                    ) : (
                      willUseShare() ? `Place & Share KOT` : `Place & Print KOT`
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table Change Modal */}
      {showTableModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div
            className="rounded-lg sm:rounded-xl max-w-4xl w-full max-h-[85vh] sm:max-h-[80vh] overflow-hidden"
            style={{ backgroundColor: currentTheme.colors.background.card }}
          >
            <div className="p-4 sm:p-6 border-b flex items-center justify-between" style={{ borderColor: currentTheme.colors.border.primary }}>
              <h2 className="text-base sm:text-xl font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                Select a Different Table
              </h2>
              <button
                onClick={() => setShowTableModal(false)}
                className="p-1.5 sm:p-2 rounded-lg transition-colors"
                style={{ backgroundColor: currentTheme.colors.background.secondary }}
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(85vh-80px)] sm:max-h-[60vh]">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {tables?.map(table => {
                  const status = getTableStatusDisplay(table);
                  const StatusIcon = status.icon;
                  const isCurrentTable = selectedTable?.id === table.id;
                  const isInactive = !table.active;

                  return (
                    <div
                      key={table.id}
                      onClick={() => handleTableSelect(table)}
                      className={clsx(
                        "p-3 sm:p-4 rounded-lg sm:rounded-xl border-2 transition-all",
                        isInactive ? "opacity-50 cursor-not-allowed" : "cursor-pointer transform hover:scale-105"
                      )}
                      style={{
                        backgroundColor: isInactive ? '#fef2f2' : (isCurrentTable ? currentTheme.colors.primary[50] : currentTheme.colors.background.card),
                        borderColor: isInactive ? '#ef4444' : (isCurrentTable ? currentTheme.colors.primary[300] : currentTheme.colors.border.primary)
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-base sm:text-lg font-bold truncate" style={{ color: currentTheme.colors.text.primary }}>
                          {table.name}
                        </span>
                        {isCurrentTable && <Check className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 ml-1" style={{ color: currentTheme.colors.primary[600] }} />}
                      </div>

                      <div className="flex items-center justify-between mb-2 gap-2">
                        <span className="text-xs sm:text-sm" style={{ color: currentTheme.colors.text.secondary }}>
                          {table.capacity} seats
                        </span>
                      </div>

                      <div className={clsx('px-2 py-1 rounded-lg text-xs font-semibold w-full text-center border', status.color)}>
                        <div className="flex items-center justify-center space-x-1">
                          <StatusIcon className="h-3 w-3" />
                          <span>{status.text}</span>
                        </div>
                      </div>

                      {(table.currentOrders?.length ?? 0) > 0 && (
                        <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded mt-2 font-medium border border-orange-200">
                          <div className="flex items-center justify-center space-x-1">
                            <Users className="h-3 w-3" />
                            <span>
                              {table.currentOrders!.length === 1
                                ? `1 Active Order (${table.currentOrders![0].items.length} items)`
                                : `${table.currentOrders!.length} Active Orders`}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Existing-order choice modal */}
      {showExistingOrderChoice && selectedTable && (selectedTable.currentOrders?.length ?? 0) > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
          <div
            className="max-w-lg w-full rounded-xl border p-5"
            style={{ backgroundColor: currentTheme.colors.background.card, borderColor: currentTheme.colors.border.primary }}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold" style={{ color: currentTheme.colors.text.primary }}>
                This table is in use
              </h3>
              <button
                onClick={() => {
                  setShowExistingOrderChoice(false);
                  setSelectedTable(null);
                }}
                className="p-1 rounded-lg transition-colors"
                style={{ backgroundColor: currentTheme.colors.background.secondary }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.hover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.colors.background.secondary}
              >
                <X className="h-5 w-5" style={{ color: currentTheme.colors.text.secondary }} />
              </button>
            </div>
            <p className="text-sm mb-4" style={{ color: currentTheme.colors.text.secondary }}>
              Do you want to update an existing order or start a new one?
            </p>

            <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
              {selectedTable.currentOrders!.map((ord) => (
                <div
                  key={ord.id}
                  className="border rounded-lg p-3 flex items-center justify-between"
                  style={{ borderColor: currentTheme.colors.border.primary, backgroundColor: currentTheme.colors.background.secondary }}
                >
                  <div>
                    <div className="text-sm font-medium" style={{ color: currentTheme.colors.text.primary }}>
                      {ord.orderNo} • ₹{ord.total.toLocaleString()}
                    </div>
                    <div className="text-xs" style={{ color: currentTheme.colors.text.secondary }}>
                      {ord.items.length} items
                    </div>
                  </div>
                  <button
                    onClick={() => loadExistingOrderForEdit(ord)}
                    className="px-3 py-1.5 rounded-lg text-white text-sm"
                    style={{ backgroundColor: currentTheme.colors.primary[600] }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700]}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600]}
                  >
                    Update this
                  </button>
                </div>
              ))}
            </div>

            <div className="flex flex-col mt-6 sm:flex-row gap-2">
              <button
                onClick={() => {
                  setIsEditingExisting(false);
                  setExistingOrder(null);
                  setSelectedItems([]); // start fresh
                  setOrderNotes('');
                  setShowExistingOrderChoice(false);
                  setShowTableModal(false);
                }}
                className="flex-1 py-2 rounded-lg text-white text-sm"
                style={{ backgroundColor: currentTheme.colors.primary[600] }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700]}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600]}
              >
                Start New Order
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
